"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMinDrawDownDecimal = getMinDrawDownDecimal;
exports.resolveUpfrontFeeConstants = resolveUpfrontFeeConstants;
exports.getMaxAllowedTermMonths = getMaxAllowedTermMonths;
exports.getSliderUwMetadataConstants = getSliderUwMetadataConstants;
exports.extractSliderOfferConstants = extractSliderOfferConstants;
exports.isBuildSliderConfigFailure = isBuildSliderConfigFailure;
exports.buildSliderConfig = buildSliderConfig;
const isNil_1 = __importDefault(require("lodash/isNil"));
const offer_1 = require("../offer");
const offerQuote_1 = require("../offerQuote");
const bounds_1 = require("./bounds");
const math_1 = require("./math");
const offerDetails_1 = require("./offerDetails");
const offerFilter_1 = require("./offerFilter");
const sliderBounds_1 = require("./sliderBounds");
/**
 * The fraction of the line the upfront fee is charged against (Python `minDrawDown`). Mirrors the
 * fee-type branch in `lineSizeConstraintUtils.getLineSizeRequiredToFundAmount`:
 * origination fees hit the whole line, first-draw fees only the activation draw, none is immaterial.
 */
function getMinDrawDownDecimal(feeType, minLineDrawDownForActivation) {
    switch (feeType) {
        case offer_1.UpfrontFeeType.origination:
            return 1;
        case offer_1.UpfrontFeeType.firstDraw:
            return minLineDrawDownForActivation;
        case offer_1.UpfrontFeeType.none:
            return 0;
        default:
            throw new Error(`unexpected upfront fee type: ${feeType}`);
    }
}
/**
 * Resolve an offer's upfront fee (origination-or-first-draw) into the decimal constants both
 * slider builders consume. `oneTimeFeeDecimal` is the resolved fee; `firstDrawFeeDecimal` is the
 * RAW first-draw fee, deliberately kept separate from the resolved one.
 */
function resolveUpfrontFeeConstants(offer, minLineDrawDownForActivation) {
    const upfrontFee = (0, offer_1.resolveUpfrontFee)({ originationFeePercent: offer.originationFeePercent, firstDrawFeePercent: offer.firstDrawFeePercent });
    return {
        oneTimeFeeDecimal: upfrontFee.percent,
        firstDrawFeeDecimal: offer.firstDrawFeePercent ?? 0,
        minDrawDownDecimal: getMinDrawDownDecimal(upfrontFee.type, minLineDrawDownForActivation),
    };
}
function getMaxAllowedTermMonths(lineSize, allowedTermMap) {
    const availableTerms = Object.keys(allowedTermMap)
        .map(Number)
        .filter((threshold) => lineSize >= threshold)
        .flatMap((threshold) => allowedTermMap[threshold]);
    return availableTerms.length > 0 ? Math.max(...availableTerms) : undefined;
}
function getSliderUwMetadataConstants(uwMetadata) {
    if (!uwMetadata.allowedInstallmentTermLengthInMonths) {
        throw new Error('allowedInstallmentTermLengthInMonths is required for slider - every policy defines this on basePolicy');
    }
    return {
        minLineSizeCents: (0, math_1.roundDecimalProduct)(uwMetadata.minLineSize, 100),
        allowedInstallmentTermLengthInMonths: uwMetadata.allowedInstallmentTermLengthInMonths,
        minLineDrawDownForActivation: uwMetadata.minLineDrawDownForActivation ?? 1,
    };
}
/**
 * A banded offer's selectable range is its bands, as underwriting solved them: the first band already starts at every floor
 * underwriting applied (policy, term, state, mega line), several of which the frontend cannot reconstruct.
 */
function extractBandedSliderOfferConstants(offer, sharedConstants) {
    const sortedBands = (offer.lineSizePricingBands ?? []).slice().sort((left, right) => left.minLineSize - right.minLineSize);
    const constants = [];
    for (const band of sortedBands) {
        const minLineSizeCents = Math.round(band.minLineSize * 100);
        const maxLineSizeCents = Math.round(band.maxLineSize * 100);
        if (band.pricing.fixedTermApr === null) {
            return { error: `line-size pricing band ${band.minLineSize}-${band.maxLineSize} has nil fixedTermApr` };
        }
        if (minLineSizeCents > maxLineSizeCents) {
            return { error: `line-size pricing band ${band.minLineSize}-${band.maxLineSize} has an empty selectable range` };
        }
        constants.push({
            ...sharedConstants,
            fixedTermApr: band.pricing.fixedTermApr,
            pricing: band.pricing,
            minLineSizeCents,
            maxLineSizeCents,
            recordingTaxFree: band.recordingTaxFree,
        });
    }
    for (let index = 1; index < constants.length; index += 1) {
        const previous = constants[index - 1];
        const current = constants[index];
        if (current.minLineSizeCents !== previous.maxLineSizeCents + 1) {
            return {
                error: `lineSizePricingBands are not contiguous between ${previous.maxLineSizeCents} and ${current.minLineSizeCents}`,
            };
        }
    }
    return constants;
}
function tryExtractSliderOfferConstants(offer, uwMetadataConstants) {
    if (!offer.fixedTermMonths || offer.fixedTermMonths <= 0) {
        return { error: `fixedTermMonths=${offer.fixedTermMonths}` };
    }
    if (offer.fixedTermApr === undefined || offer.fixedTermApr === null) {
        return { error: 'fixedTermApr is nil' };
    }
    if (!offer.identifier) {
        return { error: 'identifier is falsy' };
    }
    if (!offer.uwSpecId) {
        return { error: 'uwSpecId is falsy' };
    }
    if (!offer.lineSizePricingBands || offer.lineSizePricingBands.length === 0) {
        return { error: 'lineSizePricingBands is empty' };
    }
    // The backend accepts no line below the policy's floor, so an offer whose floor is above its bands' top has no line to select
    // (a mega-line offer whose line stops under the mega floor).
    const topLineSize = Math.max(...offer.lineSizePricingBands.map((band) => band.maxLineSize));
    if (!(0, isNil_1.default)(offer.minLineSize) && offer.minLineSize > topLineSize) {
        return { error: `minLineSize ${offer.minLineSize} is above the top of lineSizePricingBands ${topLineSize}` };
    }
    const { oneTimeFeeDecimal, firstDrawFeeDecimal, minDrawDownDecimal } = resolveUpfrontFeeConstants(offer, uwMetadataConstants.minLineDrawDownForActivation);
    if ((0, isNil_1.default)(offer.recordingChargesFixedComponentDollars) || (0, isNil_1.default)(offer.recordingChargesVariableComponentDecimal)) {
        return { error: 'recordingCharges components nil' };
    }
    const sharedConstants = {
        identifier: offer.identifier,
        offer: (0, offerDetails_1.getOfferChargeableTerms)(offer),
        uwSpecId: offer.uwSpecId,
        fixedTermMonths: offer.fixedTermMonths,
        firstDrawFeeDecimal,
        oneTimeFeeDecimal,
        minDrawDownDecimal,
        recordingTaxFixedCents: (0, math_1.roundDecimalProduct)(offer.recordingChargesFixedComponentDollars, 100),
        recordingTaxVariableDecimal: offer.recordingChargesVariableComponentDecimal,
        recordingTaxFree: false,
        // priorLienPayoffFee is only populated for refi offers (via offer_prior_lien_payoff_terms); it is multiplied by a
        // payoff that is 0 when the offer does not refinance, so defaulting to 0 is semantically equivalent.
        priorLienPayoffFee: offer.priorLienPayoffFee ?? 0,
        refiPayoffCents: (0, offerDetails_1.getOfferRefiPayoffCents)(offer),
        refiPositions: (0, offerDetails_1.getOfferRefiPositions)(offer),
        refiLiensDetails: offer.lienRefiOfferTerms?.refiLiensDetails ?? [],
        refiLenderName: (0, offerDetails_1.getOfferRefiLenderName)(offer),
        secondLienBalanceBufferPercent: offer.refiLineSizeBufferDecimal ?? offer.helocRefiDetails?.secondLienBalanceBufferPercent ?? 0,
    };
    const constants = extractBandedSliderOfferConstants(offer, sharedConstants);
    if ('error' in constants) {
        return constants;
    }
    // The builders quote both edges of every band; quote them here so an offer they would throw on is dropped by the caller.
    constants.forEach((band) => {
        (0, sliderBounds_1.minCashAndPayoffForLineSizeCents)(band.offer, band.minLineSizeCents);
        (0, offerQuote_1.cashAndPayoffForLineSizeCents)(band.offer, band.maxLineSizeCents);
    });
    return constants;
}
function tryExtractSliderOfferConstantsOrDrop(offer, uwMetadataConstants) {
    // Extraction reads the refi payoff and quotes the offer, which throws on an offer missing what the quote reads: drop that offer rather than the slider.
    try {
        return tryExtractSliderOfferConstants(offer, uwMetadataConstants);
    }
    catch (error) {
        return { error: error instanceof Error ? error.message : String(error) };
    }
}
function extractSliderOfferConstants(offers, uwMetadataConstants, drops) {
    const all = [];
    for (const offer of offers) {
        const result = tryExtractSliderOfferConstantsOrDrop(offer, uwMetadataConstants);
        if ('error' in result) {
            drops?.push({ identifier: offer.identifier, error: result.error });
        }
        else {
            all.push(...result);
        }
    }
    return all;
}
function isBuildSliderConfigFailure(result) {
    return 'error' in result;
}
function buildSliderConfig(underwritingResult, selectedDebtSwapTradelines, uwMetadataConstants, selectedFirstDrawFee, options = {}) {
    const totalOffers = underwritingResult.preQualOffers?.length ?? 0;
    const preQualOffers = (0, offerFilter_1.filterOffersByFirstDrawFee)(underwritingResult.preQualOffers ?? [], selectedFirstDrawFee);
    const drops = [];
    const fail = (error) => ({ error, totalOffers, offersAfterFeeFilter: preQualOffers.length, drops });
    const sliderOfferConstants = extractSliderOfferConstants(preQualOffers, uwMetadataConstants, drops);
    if (sliderOfferConstants.length === 0) {
        return fail('all offers dropped by extractSliderOfferConstants');
    }
    const bounds = (0, bounds_1.computeSliderBounds)(sliderOfferConstants, selectedDebtSwapTradelines, options.roundRefiAdditionalFloor);
    if ('error' in bounds) {
        return fail(bounds.error);
    }
    return {
        ...bounds,
        sliderOfferConstants,
    };
}
